import { useHandleQueryError } from '@versita/frontend/api-utils';
import { CourseWithInstances } from '@versita/shared/models';
import { useAppSelector } from '../../../state';
import { pipe, find, unless, pathOr } from 'ramda';
import {
  studentPortalApi,
  useGetCourseQuery,
} from '../../../services/generated/studentPortalApi';
import { useListCoursesQueryArgs } from '../../CourseSelect';
import { telemetry } from '../../../services/telemetry';

export const useCourseById = (courseId: string) => {
  const cachedCourse = useCachedCourse(courseId);
  const courseIdHasChanged = cachedCourse?.id !== courseId;
  const shouldFetchCourse = !cachedCourse || courseIdHasChanged;
  const fetchCourse = useHandleQueryError(useGetCourseQuery);
  const {
    data: fetchedCourse,
    isFetching: isFetchingCourse,
    isLoading: isLoadingCourse,
  } = fetchCourse({ courseId }, { skip: !shouldFetchCourse });

  // If fetchedCourse is undefined (because it was skipped),
  // default to the cached course
  const course = fetchedCourse || cachedCourse;

  return { course, isFetchingCourse, isLoadingCourse };
};

const useCachedCourse = (courseId: string) =>
  useAppSelector(
    pipe(
      pathOr([], CACHED_COURSE_STATE_PATH),
      unless(Array.isArray, (value) => {
        const pathStr = CACHED_COURSE_STATE_PATH.join('.');
        const err = new Error(
          `useCachedCourse expected an array for the state path: ${pathStr} but found a ${typeof value}. Check the response shape for the listCourses endpoint has not changed.`,
        );
        console.error(err);
        telemetry.trackException({
          exception: err,
        });
        return [];
      }),
      find<CourseWithInstances>(({ id }) => id === courseId),
    ),
  );

const CACHED_COURSE_STATE_PATH = [
  studentPortalApi.reducerPath,
  'queries',
  `${studentPortalApi.endpoints.listCourses.name}(${JSON.stringify(
    useListCoursesQueryArgs,
  )})`,
  // TODO: can we make this more robust? It will fail if the shape of the
  // response ever changes
  'data',
];
